// ─────────────────────────────────────────────────────────
//  FILTJEK-OPRYDNING — midlertidig side til at gennemgå filer der ikke
//  kunne rettes automatisk efter OneDrive-sti-kollisionen (PR #531/#532).
//  Reachable via direkte URL (/filtjek) -- bevidst ikke i sidemenuen, da
//  den kun er relevant mens oprydningen pågår.
// ─────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api';
import { T } from '../tema';

const STATUS_LABEL = {
  'ambiguøs': 'Flere mulige filer',
  'ikke_fundet': 'Ingen fil fundet',
  'mangler_stoerrelse': 'Størrelse mangler',
};
const STATUS_FARVE = {
  'ambiguøs': T.gul,
  'ikke_fundet': T.roed,
  'mangler_stoerrelse': T.dæmpet,
};

function NoteFelt({ række, onGem }) {
  const [redigerer, setRedigerer] = useState(false);
  const [tekst, setTekst] = useState(række.note || '');
  if (!redigerer) {
    return (
      <div style={{ marginTop: 6 }}>
        {række.note && <div style={{ fontSize: 11.5, color: T.dæmpet, fontStyle: 'italic', marginBottom: 4 }}>Note: {række.note}</div>}
        <button onClick={() => setRedigerer(true)} style={{ background: 'none', border: 'none', color: T.turkis, fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: "'Inter',system-ui,sans-serif" }}>
          {række.note ? 'Redigér note' : '+ tilføj note'}
        </button>
      </div>
    );
  }
  return (
    <textarea
      autoFocus
      value={tekst}
      onChange={e => setTekst(e.target.value)}
      onBlur={() => { setRedigerer(false); if (tekst.trim() !== (række.note || '')) onGem(tekst.trim()); }}
      placeholder='Fx "Rettet manuelt" eller "Bekræftet korrekt dokument"'
      rows={2}
      style={{ width: '100%', marginTop: 6, background: 'rgba(255,255,255,0.06)', border: T.skillelinje, borderRadius: 6, padding: '6px 8px', color: T.creme, fontSize: 12, fontFamily: "'Inter',system-ui,sans-serif", resize: 'vertical', boxSizing: 'border-box' }}
    />
  );
}

function ItemRække({ række, onToggle, onNote, onÅbnHaendelse }) {
  const dato = række.haendelse_dato ? new Date(række.haendelse_dato).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 16px', borderBottom: T.skillelinje, opacity: række.tjekket ? 0.5 : 1 }}>
      <button
        onClick={() => onToggle(!række.tjekket)}
        aria-label="Markér som gennemgået"
        style={{
          width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 2, padding: 0, cursor: 'pointer',
          border: `1.5px solid ${række.tjekket ? T.groen : T.dæmpet}`,
          background: række.tjekket ? T.groen : 'transparent',
          color: '#06261c', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >{række.tjekket ? '✓' : ''}</button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11.5, color: T.dæmpet, marginBottom: 3 }}>
          {række.haendelse_id && række.sag_id ? (
            <>
              {dato} · {række.haendelse_titel || '(uden titel)'}{' '}
              <button
                onClick={() => onÅbnHaendelse(række.sag_id, række.haendelse_id)}
                title="Åbn denne hændelse i sagsportalen"
                style={{ color: T.turkis, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, padding: 0, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", textDecoration: 'underline' }}
              >#{række.haendelse_id} →</button>
            </>
          ) : række.haendelse_id ? (
            <>{dato} · {række.haendelse_titel || '(uden titel)'} <span style={{ color: T.subtil }}>#{række.haendelse_id}</span></>
          ) : <span style={{ color: T.subtil }}>ingen hændelse-reference</span>}
        </div>
        <div style={{ fontSize: 13, color: T.creme, wordBreak: 'break-word', fontFamily: "ui-monospace, 'JetBrains Mono', monospace" }}>{række.filnavn}</div>
        {række.detaljer && <div style={{ fontSize: 11.5, color: T.subtil, marginTop: 4 }}>{række.detaljer}</div>}
        {række.tjekket && række.tjekket_af_navn && (
          <div style={{ fontSize: 10.5, color: T.subtil, marginTop: 4 }}>
            ✓ {række.tjekket_af_navn}{række.tjekket_kl ? ' · ' + new Date(række.tjekket_kl).toLocaleDateString('da-DK') : ''}
          </div>
        )}
        <NoteFelt række={række} onGem={onNote} />
      </div>
      <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: `${STATUS_FARVE[række.status]}22`, color: STATUS_FARVE[række.status], whiteSpace: 'nowrap', flexShrink: 0 }}>
        {STATUS_LABEL[række.status] || række.status}
      </span>
    </div>
  );
}

function SagKort({ sag, rækker, onToggle, onNote, onÅbnHaendelse }) {
  const [åben, setÅben] = useState(false);
  const tjekket = rækker.filter(r => r.tjekket).length;
  const pct = rækker.length ? Math.round(100 * tjekket / rækker.length) : 0;
  const færdig = tjekket === rækker.length;
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: T.kort_kant, borderRadius: 14, marginBottom: 10, overflow: 'hidden' }}>
      <div onClick={() => setÅben(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer' }}>
        <span style={{ color: T.subtil, fontSize: 11, transition: 'transform 0.15s', transform: åben ? 'rotate(90deg)' : 'none', display: 'inline-block', width: 12 }}>▶</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.creme, fontFamily: "ui-monospace, 'JetBrains Mono', monospace", whiteSpace: 'nowrap' }}>{sag}</span>
        <span style={{ fontSize: 13, color: T.dæmpet, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rækker[0]?.klient}</span>
        <span style={{ width: 56, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', flexShrink: 0 }}>
          <span style={{ display: 'block', height: '100%', width: pct + '%', background: T.groen }} />
        </span>
        <span style={{ fontSize: 11, color: færdig ? T.groen : T.dæmpet, flexShrink: 0, minWidth: 40, textAlign: 'right' }}>{tjekket} / {rækker.length}</span>
      </div>
      {åben && (
        <div style={{ borderTop: T.skillelinje }}>
          {rækker.map(r => (
            <ItemRække key={r.fil_id} række={r} onToggle={v => onToggle(r.fil_id, v)} onNote={note => onNote(r.fil_id, note)} onÅbnHaendelse={onÅbnHaendelse} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FiltjekOprydning({ onÅbnHaendelse }) {
  const [rækker, setRækker] = useState(null);
  const [søg, setSøg] = useState('');
  const [kunResterende, setKunResterende] = useState(false);

  const hent = useCallback(() => {
    api('filtjek').then(d => { if (d?.rækker) setRækker(d.rækker); });
  }, []);
  useEffect(() => { hent(); }, [hent]);

  const toggleItem = (fil_id, tjekket) => {
    setRækker(p => p.map(r => r.fil_id === fil_id ? { ...r, tjekket, tjekket_af_navn: tjekket ? 'Dig (gemmer…)' : null, tjekket_kl: tjekket ? new Date().toISOString() : null } : r));
    api('filtjek/' + fil_id, 'PATCH', { tjekket }).then(d => {
      if (d?.række) setRækker(p => p.map(r => r.fil_id === fil_id ? { ...r, ...d.række, tjekket_af_navn: r.tjekket_af_navn } : r));
      hent(); // henter friskt tjekket_af_navn fra serveren
    });
  };
  const gemNote = (fil_id, note) => {
    setRækker(p => p.map(r => r.fil_id === fil_id ? { ...r, note } : r));
    api('filtjek/' + fil_id, 'PATCH', { note });
  };

  const grupper = useMemo(() => {
    if (!rækker) return [];
    const m = new Map();
    rækker.forEach(r => { if (!m.has(r.sagsnummer)) m.set(r.sagsnummer, []); m.get(r.sagsnummer).push(r); });
    let liste = [...m.entries()];
    if (søg.trim()) {
      const s = søg.trim().toLowerCase();
      liste = liste.filter(([sag, rk]) => sag.toLowerCase().includes(s) || (rk[0]?.klient || '').toLowerCase().includes(s));
    }
    if (kunResterende) {
      liste = liste.filter(([, rk]) => rk.some(r => !r.tjekket));
    }
    liste.sort((a, b) => b[1].length - a[1].length);
    return liste;
  }, [rækker, søg, kunResterende]);

  const stats = useMemo(() => {
    if (!rækker) return { total: 0, tjekket: 0 };
    return { total: rækker.length, tjekket: rækker.filter(r => r.tjekket).length };
  }, [rækker]);
  const pct = stats.total ? Math.round(100 * stats.tjekket / stats.total) : 0;

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 20px 80px' }}>
      <div style={{ fontSize: 11, color: T.turkis, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Oprydning</div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: T.creme, margin: '0 0 16px' }}>Filtjek — dokumenter der kræver gennemgang</h1>

      {rækker === null ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.subtil, fontSize: 13 }}>Henter liste…</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 110, background: 'rgba(255,255,255,0.03)', border: T.kort_kant, borderRadius: 10, padding: '9px 14px' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.creme }}>{stats.total}</div>
              <div style={{ fontSize: 10.5, color: T.dæmpet }}>PUNKTER I ALT</div>
            </div>
            <div style={{ flex: 1, minWidth: 110, background: 'rgba(255,255,255,0.03)', border: T.kort_kant, borderRadius: 10, padding: '9px 14px' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.groen }}>{stats.tjekket}</div>
              <div style={{ fontSize: 10.5, color: T.dæmpet }}>GENNEMGÅET</div>
            </div>
            <div style={{ flex: 1, minWidth: 110, background: 'rgba(255,255,255,0.03)', border: T.kort_kant, borderRadius: 10, padding: '9px 14px' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.creme }}>{stats.total - stats.tjekket}</div>
              <div style={{ fontSize: 10.5, color: T.dæmpet }}>RESTERENDE</div>
            </div>
          </div>
          <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ height: '100%', width: pct + '%', background: `linear-gradient(90deg, ${T.groen}, ${T.turkis})`, transition: 'width 0.3s' }} />
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <input
              value={søg}
              onChange={e => setSøg(e.target.value)}
              placeholder="Søg på sagsnummer eller klientnavn…"
              style={{ flex: 1, minWidth: 180, background: 'rgba(255,255,255,0.06)', border: T.skillelinje, borderRadius: 8, padding: '8px 12px', color: T.creme, fontSize: 13, fontFamily: "'Inter',system-ui,sans-serif" }}
            />
            <button
              onClick={() => setKunResterende(v => !v)}
              style={{
                background: kunResterende ? 'rgba(13,148,136,0.15)' : 'rgba(255,255,255,0.04)',
                border: kunResterende ? `1px solid ${T.turkis}` : T.skillelinje,
                color: kunResterende ? T.turkis : T.dæmpet,
                borderRadius: 20, padding: '7px 14px', fontSize: 12, fontWeight: kunResterende ? 600 : 400, cursor: 'pointer', fontFamily: "'Inter',system-ui,sans-serif",
              }}
            >Kun resterende</button>
          </div>

          <div style={{ fontSize: 12, color: T.dæmpet, display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, paddingBottom: 12, borderBottom: T.skillelinje }}>
            <span><span style={{ width: 8, height: 8, borderRadius: '50%', background: T.gul, display: 'inline-block', marginRight: 6 }} />Flere mulige filer — kræver et valg</span>
            <span><span style={{ width: 8, height: 8, borderRadius: '50%', background: T.roed, display: 'inline-block', marginRight: 6 }} />Ingen fil matcher — undersøg manuelt</span>
          </div>

          {grupper.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: T.dæmpet, fontSize: 13 }}>
              {kunResterende || søg ? 'Ingen sager matcher.' : 'Ingen punkter tilbage — alt er gennemgået!'}
            </div>
          ) : (
            grupper.map(([sag, rk]) => <SagKort key={sag} sag={sag} rækker={rk} onToggle={toggleItem} onNote={gemNote} onÅbnHaendelse={onÅbnHaendelse} />)
          )}

          <div style={{ fontSize: 11.5, color: T.subtil, marginTop: 24, lineHeight: 1.6 }}>
            Baggrund: to hændelser/mails på samme sag kunne tidligere dele samme fil-reference i OneDrive, hvis de brugte samme filnavn — rettet i koden (PR #531/#532). Denne liste er de filer hvor det ikke kunne afgøres automatisk hvilken fysisk fil der hører til hvilken hændelse. Åbn sagen i portalen, find hændelsen, klik "Vis dokument", og bekræft indholdet er korrekt. Sæt flueben når en linje er tjekket — det gemmes for alle der åbner denne side.
          </div>
        </>
      )}
    </div>
  );
}
